/**
 * The gizmo's KEY RULE (W2-02; DESIGN.md §5.6.6, §5.6.1, §6.6): which keyframes a direct manipulation in the preview
 * writes, planned before the gesture starts and applied on every gesture update (each update starts again from the
 * document before the gesture, so applying the same plan with new values is idempotent).
 *
 *  - Position is evaluated as a PAIR: if X or Y is animated (two or more keys on the written layer), a free drag
 *    keys both axes at the playhead; a Shift axis lock limits the drag (and the writes) to one axis.
 *  - Other properties (and an unanimated position): no track -> the track is created (one key at beat 0: the whole
 *    chart); one key -> that key's value changes (whole chart); two or more keys -> a keyframe at the playhead is
 *    created or updated.
 *  - Auto-key: always a keyframe at the playhead (a missing track is created at the playhead; Core keeps the lead-in
 *    invariant by adding the beat-0 Stay key with the value that looks like "no track").
 *  - Ctrl ("Offset whole motion"): every keyframe of the dragged property (or only the selected keyframes of that
 *    track) moves by the delta; a missing track is created like a whole-chart edit, at what the absent track looks
 *    like plus the delta (`offsetValues`: a normal line's absent scaleX is 2, so a 1.5x drag writes 3).
 * Writes go to the active layer (Full level) or layer 0 (Guided); extended properties always use layer 0.
 */

import { Core } from '../../../core/commands/index';
import { Expr } from '../../../core/expr/index';
import { PROP_SPECS } from '../../../core/model/props';
import type { ChartDoc, DocIndexes, KeyId, LineId, LineProp, TrackId } from '../../../core/model/types';
import type { TxBuilder } from '../../../core/ops/types';
import { Beats } from '../../../core/time/beat';
import type { Beat } from '../../../core/time/types';
import { tidyValue, type Axis } from './gizmoMath';

/** What one property's write does. */
export type KeyAction =
  /** No track: create it (one key at `at`: beat 0 = whole chart; the playhead with auto-key). */
  | { readonly kind: 'create'; readonly at: Beat }
  /** One key: its value is the whole-chart value. */
  | { readonly kind: 'whole'; readonly key: KeyId }
  /** A keyframe at the playhead (inserted, or the existing key at that beat updated). */
  | { readonly kind: 'key'; readonly at: Beat; readonly existing: KeyId | null }
  /** Offset whole motion: these keys move by the delta. */
  | { readonly kind: 'offset'; readonly keys: readonly KeyId[]; readonly selectedOnly: boolean }
  /** Not written (the other axis under a Shift lock). */
  | { readonly kind: 'skip' };

export interface PropPlan {
  readonly prop: LineProp;
  readonly layer: number;
  readonly track: TrackId | null;
  readonly action: KeyAction;
}

export interface KeyPlanInput {
  readonly line: LineId;
  /** Properties the manipulation changes (position: moveX and moveY together). */
  readonly props: readonly LineProp[];
  /** Layer written for layered properties (active layer in Full, 0 in Guided). */
  readonly layer: number;
  /** The playhead beat (snapped to the grid when a key will be written). */
  readonly at: Beat;
  readonly autoKey: boolean;
  /** Ctrl+drag. */
  readonly offset: boolean;
  /** Shift axis lock of a position drag (null = free). */
  readonly lock: Axis | null;
  /** Keys currently selected (an offset moves only the selected keys of a track when it has any). */
  readonly selectedKeys: ReadonlySet<KeyId>;
}

/** Layer a property is written on. */
export function layerFor(prop: LineProp, activeLayer: number): number {
  return PROP_SPECS[prop].layered ? activeLayer : 0;
}

function keysOf(idx: DocIndexes, track: TrackId | undefined): readonly KeyId[] {
  return track === undefined ? [] : idx.keysByTrack(track);
}

function keyAt(doc: ChartDoc, keys: readonly KeyId[], at: Beat): KeyId | null {
  for (const id of keys) {
    const k = doc.keys.get(id);
    if (k !== undefined && Beats.eq(k.t, at)) return id;
  }
  return null;
}

/** Whether a property has two or more keys on the written layer. */
export function isAnimated(idx: DocIndexes, line: LineId, prop: LineProp, layer: number): boolean {
  return keysOf(idx, idx.track(line, prop, layerFor(prop, layer))).length >= 2;
}

/** The plan of every property of a manipulation (see the module comment). */
export function planKeyWrites(doc: ChartDoc, idx: DocIndexes, input: KeyPlanInput): PropPlan[] {
  const position = input.props.includes('moveX') && input.props.includes('moveY');
  const pairAnimated =
    position &&
    (isAnimated(idx, input.line, 'moveX', input.layer) || isAnimated(idx, input.line, 'moveY', input.layer));
  return input.props.map((prop): PropPlan => {
    const layer = layerFor(prop, input.layer);
    const track = idx.track(input.line, prop, layer) ?? null;
    const keys = keysOf(idx, track ?? undefined);
    const skip =
      position &&
      input.lock !== null &&
      ((prop === 'moveX' && input.lock === 'y') || (prop === 'moveY' && input.lock === 'x'));
    if (skip) return { prop, layer, track, action: { kind: 'skip' } };
    if (input.offset) {
      if (track === null) return { prop, layer, track, action: { kind: 'create', at: Beats.ZERO } };
      const selected = keys.filter((k) => input.selectedKeys.has(k));
      return {
        prop,
        layer,
        track,
        action: { kind: 'offset', keys: selected.length > 0 ? selected : keys, selectedOnly: selected.length > 0 },
      };
    }
    const keyHere = input.autoKey || (position ? pairAnimated : keys.length >= 2);
    if (keyHere) {
      if (track === null) return { prop, layer, track, action: { kind: 'create', at: input.at } };
      return { prop, layer, track, action: { kind: 'key', at: input.at, existing: keyAt(doc, keys, input.at) } };
    }
    if (track === null) return { prop, layer, track, action: { kind: 'create', at: Beats.ZERO } };
    const only = keys[0];
    if (keys.length === 1 && only !== undefined) return { prop, layer, track, action: { kind: 'whole', key: only } };
    return { prop, layer, track, action: { kind: 'key', at: input.at, existing: keyAt(doc, keys, input.at) } };
  });
}

/** True when any property of the plan writes a keyframe at the playhead (the playhead snaps to the grid first). */
export function writesAtPlayhead(plans: readonly PropPlan[]): boolean {
  return plans.some(
    (p) => p.action.kind === 'key' || (p.action.kind === 'create' && !Beats.eq(p.action.at, Beats.ZERO)),
  );
}

/** Formats a delta for the offset expression (`+= d`), without exponents. */
function deltaSource(d: number): string {
  const s = d.toFixed(6).replace(/\.?0+$/, '');
  return `+= ${s === '-0' || s === '' ? '0' : s}`;
}

function shiftKeys(tx: TxBuilder, keys: readonly KeyId[], delta: number): void {
  if (delta === 0 || keys.length === 0) return;
  const parsed = Expr.parse(deltaSource(delta), 'keyField');
  if (!parsed.ok) tx.fail(parsed.error);
  const prog = parsed.value;
  // Leave values first; arrive values of keys whose previous segment is not a Stay next (a Stay's next arrive value
  // follows its leave value automatically, so shifting it again would move it twice).
  Core.keys.exprKeys(tx, { keys, part: 'out', prog, seed: 0 });
  const arrive = keys.filter((id) => {
    const prev = tx.idx.prevKey(id);
    if (prev === undefined) return false;
    return tx.doc.keys.get(prev)?.ease.kind !== 'hold';
  });
  if (arrive.length > 0) Core.keys.exprKeys(tx, { keys: arrive, part: 'in', prog, seed: 0 });
}

/**
 * The values `applyKeyPlan` takes for a Ctrl "Offset whole motion" update, from the DELTA of each property and its
 * start value (on its written layer at the edit time; for a missing track, what the absent track looks like):
 * 'offset' actions move their keys by the delta; any other action writes an absolute value, `start + delta`. Only a
 * missing track ('create') takes the second path under an offset plan, and taking the delta as its absolute value
 * would be wrong for every property whose absent value is not 0 (a normal line's absent scaleX is 2: a 1.5x drag
 * must write 3, not 1, which would draw the line at half its length).
 */
export function offsetValues(
  plans: readonly PropPlan[],
  deltas: ReadonlyMap<LineProp, number>,
  starts: ReadonlyMap<LineProp, number>,
): Map<LineProp, number> {
  const out = new Map<LineProp, number>();
  for (const [prop, delta] of deltas) {
    const plan = plans.find((p) => p.prop === prop);
    if (plan === undefined || plan.action.kind === 'offset' || plan.action.kind === 'skip') {
      out.set(prop, delta);
      continue;
    }
    out.set(prop, tidyValue((starts.get(prop) ?? 0) + delta));
  }
  return out;
}

/**
 * Applies a plan with the target value of each property: absolute values, except 'offset' actions, whose value is
 * the DELTA (`offsetValues` builds the values of an offset update from deltas). Properties missing from `values` are
 * left alone.
 */
export function applyKeyPlan(
  tx: TxBuilder,
  line: LineId,
  plans: readonly PropPlan[],
  values: ReadonlyMap<LineProp, number>,
): void {
  for (const plan of plans) {
    const value = values.get(plan.prop);
    if (value === undefined || !Number.isFinite(value)) continue;
    const a = plan.action;
    const owner = { kind: 'line', line, prop: plan.prop, layer: plan.layer } as const;
    switch (a.kind) {
      case 'skip':
        break;
      case 'create': {
        const existing = tx.idx.trackOfOwner(owner);
        if (existing === undefined) {
          Core.keys.ensureTrack(tx, { owner, value, at: a.at });
        } else {
          // An earlier write of this update created the track (the X/Y link mirrors a new axis): key it there.
          writeKeyAt(tx, existing, a.at, value);
        }
        break;
      }
      case 'whole':
        Core.keys.setKeyValues(tx, { keys: [a.key], part: 'both', value });
        break;
      case 'key': {
        const track = tx.idx.trackOfOwner(owner);
        if (track === undefined) Core.keys.ensureTrack(tx, { owner, value, at: a.at });
        else writeKeyAt(tx, track, a.at, value);
        break;
      }
      case 'offset':
        shiftKeys(tx, a.keys, value);
        break;
    }
  }
}

/** Creates or updates the key at `at` of a track (the visible value at the playhead: a jump key's leave value). */
function writeKeyAt(tx: TxBuilder, track: TrackId, at: Beat, value: number): void {
  for (const id of tx.idx.keysByTrack(track)) {
    const k = tx.doc.keys.get(id);
    if (k === undefined || !Beats.eq(k.t, at)) continue;
    const jump = k.vIn !== k.vOut && tx.idx.prevKey(id) !== undefined;
    Core.keys.setKeyValues(tx, { keys: [id], part: jump ? 'out' : 'both', value });
    return;
  }
  Core.keys.insertKey(tx, { track, t: at, value });
}

/** Number of keys a plan touches (hint text "offsets 12 keyframes"). */
export function offsetCount(plan: PropPlan): number {
  return plan.action.kind === 'offset' ? plan.action.keys.length : 0;
}
